import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, BehaviorSubject } from 'rxjs';
import { environment } from '../../environments/environment';

export interface SendCodeRequest {
  email: string;
}

export interface SendCodeResponse {
  message: string;
}

export interface RegisterVerifyRequest {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  code: string;
}

export interface RegisterVerifyResponse {
  message: string;
}

export interface LoginRequest {
  email: string;
  password: string;
}

export interface LoginResponse {
  message?: string;
  token?: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  [key: string]: any; // API yanıtında başka alanlar da olabilir
}

export interface ForgotPasswordSendCodeRequest {
  email: string;
}

export interface ForgotPasswordSendCodeResponse {
  message: string;
}

export interface ResetPasswordWithCodeRequest {
  email: string;
  code: string;
  newPassword: string;
}

export interface ResetPasswordWithCodeResponse {
  message: string;
}

export interface User {
  firstName?: string;
  lastName?: string;
  email: string;
  token?: string;
}

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private apiUrl = `${environment.apiUrl}/Auth`;
  private readonly STORAGE_KEY = 'currentUser';
  
  private currentUserSubject = new BehaviorSubject<User | null>(this.getStoredUser());
  public currentUser$ = this.currentUserSubject.asObservable();

  constructor(private http: HttpClient) {
    // Sayfa yüklendiğinde localStorage'dan kullanıcı bilgisini yükle
    const storedUser = this.getStoredUser();
    if (storedUser) {
      this.currentUserSubject.next(storedUser);
    }
  }

  /**
   * localStorage'dan kullanıcı bilgisini al
   */
  private getStoredUser(): User | null {
    try {
      const userStr = localStorage.getItem(this.STORAGE_KEY);
      return userStr ? JSON.parse(userStr) : null;
    } catch {
      return null;
    }
  }

  /**
   * Kullanıcı bilgisini sakla
   */
  setCurrentUser(user: User): void {
    localStorage.setItem(this.STORAGE_KEY, JSON.stringify(user));
    this.currentUserSubject.next(user);
  }

  /**
   * Mevcut kullanıcı bilgisini al
   */
  getCurrentUser(): User | null {
    return this.currentUserSubject.value;
  }

  /**
   * Kullanıcı giriş yapmış mı?
   */
  isAuthenticated(): boolean {
    return this.getCurrentUser() !== null;
  }

  /**
   * JWT token'dan rol listesini çıkar
   */
  getRoles(): string[] {
    const token = this.getCurrentUser()?.token;
    if (!token) return [];
    try {
      const payload = JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
      const roleClaim = payload['role']
        ?? payload['roles']
        ?? payload['http://schemas.microsoft.com/ws/2008/06/identity/claims/role'];
      if (!roleClaim) return [];
      return Array.isArray(roleClaim) ? roleClaim : [roleClaim];
    } catch {
      return [];
    }
  }

  hasRole(role: string): boolean {
    return this.getRoles().includes(role);
  }

  hasAnyRole(...roles: string[]): boolean {
    const userRoles = this.getRoles();
    return roles.some(r => userRoles.includes(r));
  }

  /**
   * Kullanıcı admin veya satış danışmanı mı? (Panel erişimi)
   */
  isStaff(): boolean {
    return this.hasAnyRole('Admin', 'SatisDanismani');
  }

  /**
   * Kullanıcı adını al (firstName + lastName veya email)
   */
  getUserDisplayName(): string {
    const user = this.getCurrentUser();
    if (!user) return '';
    
    if (user.firstName && user.lastName) {
      return `${user.firstName} ${user.lastName}`;
    } else if (user.firstName) {
      return user.firstName;
    } else {
      return user.email.split('@')[0]; // Email'in @ öncesi kısmı
    }
  }

  /**
   * Çıkış yap
   */
  logout(): void {
    localStorage.removeItem(this.STORAGE_KEY);
    this.currentUserSubject.next(null);
  }

  /**
   * 1. Adım: E-posta adresine doğrulama kodu gönder
   */
  sendVerificationCode(email: string): Observable<SendCodeResponse> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json'
    });

    const body: SendCodeRequest = { email };

    return this.http.post<SendCodeResponse>(
      `${this.apiUrl}/register-send-code`,
      body,
      {
        headers,
        withCredentials: true
      }
    );
  }

  /**
   * 2. Adım: Kodu doğrula ve kullanıcıyı oluştur
   */
  verifyCodeAndRegister(data: RegisterVerifyRequest): Observable<RegisterVerifyResponse> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json'
    });

    return this.http.post<RegisterVerifyResponse>(
      `${this.apiUrl}/register-verify`,
      data,
      {
        headers,
        withCredentials: true
      }
    );
  }

  /**
   * Kullanıcı girişi
   */
  login(email: string, password: string): Observable<LoginResponse> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json'
    });

    const body: LoginRequest = { email, password };

    return this.http.post<LoginResponse>(
      `${this.apiUrl}/login`,
      body,
      {
        headers,
        withCredentials: true
      }
    );
  }

  /**
   * Google ID token ile backend'e giriş yap, JWT al
   */
  googleLogin(idToken: string): Observable<LoginResponse> {
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    return this.http.post<LoginResponse>(
      `${this.apiUrl}/google-login`,
      { idToken },
      { headers, withCredentials: true }
    );
  }

  /**
   * Şifremi Unuttum - Adım 1: E-posta adresine doğrulama kodu gönder
   */
  forgotPasswordSendCode(email: string): Observable<ForgotPasswordSendCodeResponse> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json'
    });

    const body: ForgotPasswordSendCodeRequest = { email };

    return this.http.post<ForgotPasswordSendCodeResponse>(
      `${this.apiUrl}/forgot-password-send-code`,
      body,
      {
        headers,
        withCredentials: true
      }
    );
  }

  /**
   * Şifremi Unuttum - Adım 2: Kod ile şifre sıfırla
   */
  resetPasswordWithCode(email: string, code: string, newPassword: string): Observable<ResetPasswordWithCodeResponse> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json'
    });

    const body: ResetPasswordWithCodeRequest = { email, code, newPassword };

    return this.http.post<ResetPasswordWithCodeResponse>(
      `${this.apiUrl}/reset-password-with-code`,
      body,
      {
        headers,
        withCredentials: true
      }
    );
  }
}
